import { Component, useCallback, useEffect, useState, type ComponentType, type ErrorInfo, type ReactNode } from "react";
import type { ExtensionManifest } from "@k8s-dashboard/extensions";
import { useExtensionCapabilitySnapshot } from "@/api/capabilities";
import { ErrorState, LoadingState } from "@/components/SectionState";
import {
  EXTENSION_DISABLED_LABEL,
  evictExtensionModule,
  extensionAvailability,
  extensionRouteEntry,
  loadExtensionModule,
  resolveExtensionComponent,
  type ExtensionDisabledReason,
} from "./registry";
import { recordExtensionEvent } from "./telemetry";

/**
 * 확장 라우트 호스트 (ADR 0024)
 * --------------------------------------------------------------------------
 * 확장 화면 하나를 **격리해서** 엽니다. 격리의 내용은 다섯입니다.
 *
 *   - 유계 확인   서버 능력을 확인하는 동안은 로딩이고, 그 대기도 시간 제한이 있습니다.
 *   - 지연 로드   선택하기 전에는 청크를 내려받지 않습니다.
 *   - 시간 제한   무한 스피너 대신 유계 대기 후 재시도 가능한 상태를 보여줍니다.
 *   - 비활성 사유 서버 능력과 맞지 않으면 **왜 꺼졌는지**를 문장으로 보여줍니다.
 *   - 오류 격리   확장이 렌더 중 터져도 앱 셸과 다른 라우트는 그대로 삽니다.
 *
 * 확장은 여기서 어떤 권한도 얻지 않습니다 — 데이터는 기존 BFF 통로로만 오고,
 * 접근 통제는 서버가 요청마다 강제합니다.
 */

/** 로드 대기 상한. 넘으면 스피너를 계속 돌리지 않고 상태를 바꿉니다. */
export const EXTENSION_LOAD_TIMEOUT_MS = 10_000;

type LoadState =
  | { status: "loading" }
  | { status: "ready"; Component: ComponentType }
  | { status: "timeout" }
  | { status: "error" };

/** 확장 화면이 터져도 라우터와 앱 셸은 살아 있어야 합니다. */
class ExtensionErrorBoundary extends Component<{ extensionId: string; children: ReactNode }, { crashed: boolean }> {
  state = { crashed: false };

  static getDerivedStateFromError() {
    return { crashed: true };
  }

  /* 오류 내용은 계측에 싣지 않습니다 — 라벨은 닫힌 집합이어야 합니다. */
  componentDidCatch(_error: Error, _info: ErrorInfo) {
    recordExtensionEvent(this.props.extensionId, "render_failed");
  }

  render() {
    if (!this.state.crashed) return this.props.children;
    return <ErrorState detail="확장 화면에서 오류가 발생했습니다. 다른 화면은 계속 사용할 수 있습니다." />;
  }
}

function ExtensionDisabled({
  extensionId,
  reason,
  onRetry,
}: {
  extensionId: string;
  reason: ExtensionDisabledReason;
  onRetry?: () => void;
}) {
  useEffect(() => {
    recordExtensionEvent(extensionId, "disabled");
  }, [extensionId]);
  return (
    <div className="state state--degraded">
      <span className="state__glyph" aria-hidden="true">
        ▲
      </span>
      <span className="state__title">사용할 수 없는 기능입니다</span>
      <span className="state__detail">
        {EXTENSION_DISABLED_LABEL[reason]} 데이터가 없는 것도, 권한이 없는 것도 아닙니다 — 서버와 화면의 기능 계약이 맞지 않습니다.
      </span>
      {onRetry && (
        <button type="button" className="linkish" onClick={onRetry}>
          다시 확인
        </button>
      )}
    </div>
  );
}

/** 지연 모듈 하나를 상태 기계로 가져옵니다. attempt가 바뀌면 다시 시도합니다. */
function useExtensionModule(manifest: ExtensionManifest, componentName: string, attempt: number): LoadState {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  useEffect(() => {
    let active = true;
    setState({ status: "loading" });
    recordExtensionEvent(manifest.id, "load_started");
    const timer = window.setTimeout(() => {
      if (!active) return;
      recordExtensionEvent(manifest.id, "load_timeout");
      setState({ status: "timeout" });
    }, EXTENSION_LOAD_TIMEOUT_MS);
    loadExtensionModule(manifest).then(
      (exports) => {
        if (!active) return;
        window.clearTimeout(timer);
        const Loaded = resolveExtensionComponent(exports, componentName);
        if (!Loaded) {
          /* 선언한 export가 없거나 컴포넌트가 아닙니다 — 로드 실패와 같게 다룹니다. */
          recordExtensionEvent(manifest.id, "load_failed");
          setState({ status: "error" });
          return;
        }
        recordExtensionEvent(manifest.id, "load_succeeded");
        setState({ status: "ready", Component: Loaded });
      },
      () => {
        if (!active) return;
        window.clearTimeout(timer);
        recordExtensionEvent(manifest.id, "load_failed");
        setState({ status: "error" });
      },
    );
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [manifest, componentName, attempt]);
  return state;
}

function ExtensionModuleView({ manifest, component }: { manifest: ExtensionManifest; component: string }) {
  const [attempt, setAttempt] = useState(0);
  /* 캐시된 약속을 버리고 나서 다시 셉니다 — 버리지 않으면 "다시 시도"가 끝나지 않은
     같은 약속을 또 기다립니다. 이전 시도의 effect는 이미 정리되어 늦게 끝나도 조용합니다. */
  const retry = useCallback(() => {
    evictExtensionModule(manifest);
    setAttempt((n) => n + 1);
  }, [manifest]);
  const state = useExtensionModule(manifest, component, attempt);
  if (state.status === "loading") return <LoadingState />;
  if (state.status === "timeout") {
    return (
      <div className="state state--degraded">
        <span className="state__glyph" aria-hidden="true">
          ▲
        </span>
        <span className="state__title">불러오는 데 오래 걸립니다</span>
        <span className="state__detail">네트워크가 느리거나 자산을 받지 못했습니다. 다른 화면은 계속 사용할 수 있습니다.</span>
        <button type="button" className="linkish" onClick={retry}>
          다시 시도
        </button>
      </div>
    );
  }
  if (state.status === "error") return <ErrorState detail="확장 화면을 불러오지 못했습니다." onRetry={retry} />;
  const Loaded = state.Component;
  return <Loaded />;
}

/**
 * 능력 확인 대기. 무한 스피너가 되지 않도록 같은 상한을 걸고, 넘으면 사유와 재조회를
 * 보여줍니다 — nav에서는 이미 감춰져 있으므로 여기 오는 것은 직접 진입뿐입니다.
 */
function ExtensionPending({ extensionId, onRetry }: { extensionId: string; onRetry: () => void }) {
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setExpired(true), EXTENSION_LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, []);
  if (!expired) return <LoadingState />;
  return <ExtensionDisabled extensionId={extensionId} reason="capabilities_unavailable" onRetry={onRetry} />;
}

/** 라우터가 거는 진입점. 확장 id와 라우트 id는 매니페스트가 선언한 값입니다. */
export function ExtensionRoute({ extensionId, routeId }: { extensionId: string; routeId: string }) {
  const entry = extensionRouteEntry(extensionId, routeId);
  const { snapshot, refetch } = useExtensionCapabilitySnapshot();
  const availability = extensionAvailability(entry?.manifest, snapshot);
  if (availability.status === "pending") return <ExtensionPending extensionId={extensionId} onRetry={refetch} />;
  if (availability.status === "disabled" || !entry) {
    const reason: ExtensionDisabledReason = availability.status === "disabled" ? availability.reason : "unknown_extension";
    const retry = reason === "capabilities_unavailable" ? refetch : undefined;
    return <ExtensionDisabled extensionId={extensionId} reason={reason} onRetry={retry} />;
  }
  return (
    <ExtensionErrorBoundary extensionId={extensionId}>
      <ExtensionModuleView manifest={entry.manifest} component={entry.route.component} />
    </ExtensionErrorBoundary>
  );
}
